import React, { useState, useEffect } from "react";
import Modal from "components/modal";

const DepenseFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialDepense, 
  isEditing,
  loading 
}) => {
  const [depense, setDepense] = useState({
    designation: "",
    qte_depense: 1,
    pu_depense: "",
    desc_depense: "",
    date_depense: new Date().toISOString().split('T')[0],
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && initialDepense) {
      setDepense({
        designation: initialDepense.designation || "",
        qte_depense: initialDepense.qte_depense || 1,
        pu_depense: initialDepense.pu_depense || "",
        desc_depense: initialDepense.desc_depense || "",
        date_depense: initialDepense.date_depense || new Date().toISOString().split('T')[0],
      });
      setValidationErrors({});
    }
  }, [isOpen, initialDepense]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "qte_depense") {
      // Accepter uniquement des nombres entiers positifs
      const val = parseInt(value, 10);
      if (isNaN(val)) {
        setDepense({ ...depense, [name]: "" });
      } else {
        setDepense({ ...depense, [name]: Math.max(1, val) });
      }
    } else if (name === "pu_depense") {
      // Ne permettre que des nombres et un seul point décimal
      const regex = /^[0-9]*\.?[0-9]*$/;
      if (value === '' || regex.test(value)) {
        setDepense({ ...depense, [name]: value });
      }
    } else {
      setDepense({ ...depense, [name]: value });
    }

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!depense.designation.trim()) {
      errors.designation = "La désignation est requise";
    }

    if (!depense.qte_depense || depense.qte_depense < 1) {
      errors.qte_depense = "La quantité doit être au moins de 1";
    }

    if (!depense.pu_depense || isNaN(parseFloat(depense.pu_depense))) {
      errors.pu_depense = "Le prix unitaire est requis et doit être un nombre valide";
    } else if (parseFloat(depense.pu_depense) <= 0) {
      errors.pu_depense = "Le prix unitaire doit être supérieur à 0";
    }

    if (!depense.date_depense) {
      errors.date_depense = "La date est requise";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateTotal = () => {
    const quantity = parseFloat(depense.qte_depense) || 0;
    const price = parseFloat(depense.pu_depense) || 0;
    return quantity * price;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      designation: depense.designation,
      qte_depense: depense.qte_depense,
      pu_depense: depense.pu_depense,
      desc_depense: depense.desc_depense,
      date_depense: depense.date_depense,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  // Fonction pour formater le montant en devise (MGA)
  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount)) return "0 Ar";
    
    return new Intl.NumberFormat('fr-MG', {
      style: 'currency',
      currency: 'MGA',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numAmount);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier la dépense"
              : "Créer une nouvelle dépense"}
          </h3>
          <div className="h-1 w-12 bg-[#57A863] mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="designation"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Désignation
            </label>
            <input
              type="text"
              name="designation"
              id="designation"
              value={depense.designation}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              placeholder="Ex: Achat de fournitures, Transport..."
            />
            {validationErrors.designation && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.designation}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="qte_depense"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Quantité
              </label>
              <input
                type="number"
                name="qte_depense"
                id="qte_depense"
                min="1"
                value={depense.qte_depense}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              />
              {validationErrors.qte_depense && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.qte_depense}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="pu_depense"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Prix unitaire (MGA)
              </label>
              <div className="relative mt-1 rounded-md shadow-sm">
                <input
                  type="text"
                  name="pu_depense"
                  id="pu_depense"
                  value={depense.pu_depense}
                  onChange={handleInputChange}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
                  placeholder="Ex: 15000"
                  inputMode="decimal"
                />
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  <span className="text-gray-500 sm:text-sm">Ar</span>
                </div>
              </div>
              {validationErrors.pu_depense && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.pu_depense}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="date_depense"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Date
            </label>
            <input
              type="date"
              name="date_depense"
              id="date_depense"
              value={depense.date_depense}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
            />
            {validationErrors.date_depense && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.date_depense}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="desc_depense"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description (optionnel)
            </label>
            <textarea
              name="desc_depense"
              id="desc_depense"
              value={depense.desc_depense}
              onChange={handleInputChange}
              rows="3"
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              placeholder="Détails supplémentaires sur cette dépense..."
            />
          </div>

          <div className="rounded-md bg-blue-50 p-4 border border-blue-100">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-blue-800">Montant total</h3>
                <div className="mt-2 text-sm text-blue-700">
                  <p className="font-bold text-xl">
                    {formatCurrency(calculateTotal())}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#57A863] text-white hover:bg-[#4c9657] transition-colors shadow-sm"
              disabled={submitting || loading}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default DepenseFormModal;